/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, FormLabel, InputAdornment, styled } from "@mui/material";
import React, { useRef } from "react";
import { useSelector } from "react-redux";

import Checkbox from "../../../../components/Checkbox";
import DateRangePicker, { DateRangeType } from "../../../../components/DateRangePicker";
import { FromTo, ValueAndSetValue } from "../../../../components/Filters/types";
import Input from "../../../../components/Input";
import Typography from "../../../../components/Typography";
import { selectRemoteConfig } from "../../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../../testIds";
import { unflattenCategories } from "../../../typesense/utils";
import { PROMOTION_TRADER_LABEL } from "../../constants";

import { SelectableMenuItem } from "./SelectableMenuItem";

const FilterWrapper = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: "12px",
  maxWidth: "336px",
  [theme.breakpoints.down("md")]: {
    maxWidth: "unset",
  },
}));

export interface DesktopDateRangeProps {
  label?: string;
  dateRange?: DateRangeType;
  handleDateRange: (dateRange: DateRangeType) => void;
}

export interface DesktopCategoryMenuProps {
  filters: { categories: string[] };
  selectedFilters: ValueAndSetValue<{
    category: string;
  }>;
  showSubCategories?: boolean;
}

interface DesktopPriceRangeProps {
  selectedFilters: ValueAndSetValue<{
    priceRange: FromTo<number>;
  }>;
  label?: string;
}

const isInputValid = (value: number) => !isNaN(value) && value >= 0 && value < 100000000;

export const DesktopPriceRangeFilter: React.FC<DesktopPriceRangeProps> = ({
  selectedFilters,
  label,
}) => {
  return (
    <FilterWrapper>
      {label && <FormLabel>{label}</FormLabel>}
      <Box display="inline-flex" gap={"11px"} alignItems="center">
        <Input
          outlineInputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          disableError
          label=""
          setValue={(v) => {
            if (!v) {
              selectedFilters.priceRange.setValue({
                ...selectedFilters.priceRange.value,
                from: undefined,
              });
            } else if (v.valueOf()) {
              const inputValue = parseFloat(v.valueOf() as string);
              if (isInputValid(inputValue)) {
                selectedFilters.priceRange.setValue({
                  ...selectedFilters.priceRange.value,
                  from: inputValue,
                });
              }
            }
          }}
          placeholder="From"
          testId={TestIds.components.filters.fromPrice}
          type="number"
          value={selectedFilters.priceRange.value.from?.toString() ?? ""}
        />

        <Typography variant="body2" component="span" color="primary800">
          -
        </Typography>
        <Input
          outlineInputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          disableError
          label=""
          placeholder="To"
          setValue={(v) => {
            if (!v) {
              selectedFilters.priceRange.setValue({
                ...selectedFilters.priceRange.value,
                to: undefined,
              });
            } else if (v.valueOf()) {
              const inputValue = parseFloat(v.valueOf() as string);
              if (isInputValid(inputValue)) {
                selectedFilters.priceRange.setValue({
                  ...selectedFilters.priceRange.value,
                  to: inputValue,
                });
              }
            }
          }}
          testId={TestIds.components.filters.toPrice}
          type="number"
          value={selectedFilters.priceRange.value.to?.toString() ?? ""}
        />
      </Box>
    </FilterWrapper>
  );
};
export const DesktopCategoryMenu: React.FC<DesktopCategoryMenuProps> = ({
  selectedFilters,
  filters,
  showSubCategories = false,
}) => {
  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    selectedFilters.category.value?.split(">").at(0) ?? null
  );
  const [selectedSubCategory, setSelectedSubCategory] = React.useState<string | null>(
    selectedFilters.category.value?.split(">").at(1) ?? null
  );
  const unflattenedCategories = unflattenCategories(filters.categories);

  const ref = useRef<HTMLDivElement>(null);

  const handleCategoryClick = (category: string) => {
    if (selectedCategory === category) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
    }

    selectedFilters.category.setValue(category);
  };

  const handleSubCategoryClick = (category: string) => {
    if (selectedSubCategory === category) {
      setSelectedSubCategory(null);
    } else {
      setSelectedSubCategory(category);
    }
    if (selectedCategory) {
      selectedFilters.category.setValue(selectedCategory + ">" + category);
    }
  };

  return (
    <Box
      display="grid"
      gap="12px"
      gridTemplateColumns={showSubCategories ? "1fr 1fr" : "1fr"}
      ref={ref}
    >
      <Box maxHeight={300} overflow="scroll" className="scrollbox">
        {Object.keys(unflattenedCategories).map((category: string) => {
          const selectedCategory = selectedFilters.category.value?.split(">").at(0) === category;

          return (
            <SelectableMenuItem
              key={category}
              label={category}
              selected={selectedCategory}
              onToggle={() => handleCategoryClick(category)}
              testId={TestIds.components.filters.categoryDropdownMenuItem}
              dataId={TestIds.components.filters.categoryDropdownMenuItemDataId(category)}
            />
          );
        })}
      </Box>
      {selectedCategory && unflattenedCategories[selectedCategory] && (
        <Box maxHeight={300} overflow="scroll" className="scrollbox">
          {unflattenedCategories[selectedCategory].map((subcategory: string) => {
            const selectedCategory =
              selectedFilters.category.value?.split(">").at(1) === subcategory;

            return (
              <SelectableMenuItem
                key={subcategory}
                label={subcategory}
                selected={selectedCategory}
                onToggle={() => handleSubCategoryClick(subcategory)}
                testId={TestIds.components.filters.categoryDropdownMenuItem}
                dataId={TestIds.components.filters.categoryDropdownMenuItemDataId(subcategory)}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
export interface DesktopPromotionMenuProps {
  selectedFilters: {
    promotions: { value: string | undefined; setValue: (value: string) => void };
  };
}

export const DesktopPromotionMenu: React.FC<DesktopPromotionMenuProps> = ({ selectedFilters }) => {
  const { traderDealsFilterValue } = useSelector(selectRemoteConfig);

  const options = [{ label: PROMOTION_TRADER_LABEL, value: traderDealsFilterValue }];

  return (
    <Box>
      {options.map((option) => {
        const selected = selectedFilters.promotions.value === option.value;
        return (
          <Checkbox
            label={option.label}
            checked={selected}
            onChange={(_, checked) =>
              selectedFilters?.promotions.setValue(checked ? option.value : "")
            }
            data-testid={TestIds.components.filters.promotionMenuItem}
            sx={{
              marginLeft: 0,
            }}
          />
        );
      })}
    </Box>
  );
};

export const DesktopDateRangePicker: React.FC<DesktopDateRangeProps> = ({
  dateRange,
  handleDateRange,
  label,
}) => {
  return (
    <FilterWrapper>
      <DateRangePicker
        label={label}
        testId={TestIds.components.filters.dateSelector}
        dateRange={
          dateRange ?? {
            from: undefined,
            to: undefined,
          }
        }
        setDateRange={handleDateRange}
      />
    </FilterWrapper>
  );
};

export interface DesktopInStockFilterProps {
  selectedFilters?: ValueAndSetValue<{
    inStock: boolean;
  }>;
}

export const DesktopInStockFilter: React.FC<DesktopInStockFilterProps> = ({ selectedFilters }) => {
  return (
    <Checkbox
      label="In stock only"
      checked={selectedFilters?.inStock.value}
      onChange={(_, checked) => selectedFilters?.inStock.setValue(checked)}
      data-testid={TestIds.components.filters.inStockCheckbox}
      sx={{
        marginTop: -2,
        margin: "-6px 0px 0px 6px!important",
        "& label": {},
      }}
    />
  );
};
